import { useState } from "react";

import { useTranslations } from "next-intl";

import { twMerge } from "@/utils/twMerge";

interface ImageDndAreaProps {
  /**
   * Callback function triggered when files are dropped
   * @param {File[]} files - Array of File objects that were dropped
   */
  onFileDropped: (files: File[]) => void;
  /**
   * Optional child elements to render inside the drag area
   * @optional
   * @default undefined
   */
  children?: React.ReactNode;
  /**
   * Optional class name to apply to the drag area
   * @optional
   * @default undefined
   */
  className?: string;
  /**
   * Optional content shown in the drag-over overlay
   * @optional
   * @default translated "drop image here" text
   */
  placeholder?: React.ReactNode;
}

export const ImageDndArea = ({
  onFileDropped,
  className,
  children,
  placeholder,
}: ImageDndAreaProps) => {
  const t = useTranslations();
  const [isDragOver, setIsDragOver] = useState(false);

  const hasFiles = (e: React.DragEvent) => e.dataTransfer.types.includes("Files");

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasFiles(e)) return;

    setIsDragOver(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!hasFiles(e)) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFileDropped(files);
    }
  };

  return (
    <div
      data-file-drop-area
      className={twMerge(
        "relative",
        isDragOver && "[--color-block-border:var(--color-blue-500)]",
        className,
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDragOver && (
        <div
          className={twMerge(
            "absolute z-100 rounded-12 inset-0 flex items-center justify-center bg-block-bg opacity-90",
          )}
        >
          {placeholder ?? (
            <div className="flex flex-col items-center gap-2.5 text-document-text tracking-tight text-[17px]">
              {t("doc.image.dnd-placeholder")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
